import React, { useState, useEffect, useCallback } from 'react';
import { DepositItem, texasComplianceService } from '../../services/texasComplianceService';

export interface DepositTrackerWidgetProps {
  onDepositClick?: (deposit: DepositItem) => void;
  onPropertyClick?: (propertyId: string) => void;
  initialData?: DepositItem[];
  refreshIntervalMs?: number; // Defaults to 5 minutes (300,000 ms) per T-222-001
}

export const DepositTrackerWidget: React.FC<DepositTrackerWidgetProps> = ({
  onDepositClick,
  onPropertyClick,
  initialData,
  refreshIntervalMs = 5 * 60 * 1000,
}) => {
  const [deposits, setDeposits] = useState<DepositItem[]>(initialData || []);
  const [loading, setLoading] = useState<boolean>(!initialData);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);

  const fetchDeposits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await texasComplianceService.getDepositTracker();
      setDeposits(data);
      setLastRefreshed(new Date());
    } catch (err: any) {
      setError(err?.message || 'Failed to load deposit deadlines');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initialData) {
      fetchDeposits();
    }
    const interval = setInterval(fetchDeposits, refreshIntervalMs);
    return () => clearInterval(interval);
  }, [fetchDeposits, initialData, refreshIntervalMs]);

  // Sort by urgency: overdue first, then lowest daysRemaining
  const sortedDeposits = [...deposits].sort((a, b) => {
    if (a.daysRemaining < 0 && b.daysRemaining >= 0) return -1;
    if (b.daysRemaining < 0 && a.daysRemaining >= 0) return 1;
    return a.daysRemaining - b.daysRemaining;
  });

  const getStatusBadge = (item: DepositItem) => {
    if (item.daysRemaining < 0) {
      return (
        <span
          data-testid="status-badge-overdue"
          className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black text-white uppercase tracking-wider"
        >
          {Math.abs(item.daysRemaining)}d Overdue
        </span>
      );
    }
    if (item.daysRemaining < 7) {
      return (
        <span
          data-testid="status-badge-critical"
          className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-red-100 text-red-700 border border-red-200"
        >
          {item.daysRemaining}d Left (Critical)
        </span>
      );
    }
    if (item.daysRemaining <= 14) {
      return (
        <span
          data-testid="status-badge-warning"
          className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-yellow-100 text-yellow-800 border border-yellow-200"
        >
          {item.daysRemaining}d Left (Warning)
        </span>
      );
    }
    return (
      <span
        data-testid="status-badge-normal"
        className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-700 border border-emerald-200"
      >
        {item.daysRemaining}d Left (On Track)
      </span>
    );
  };

  const getProgressBarColor = (daysRemaining: number): string => {
    if (daysRemaining < 0) return 'bg-black';
    if (daysRemaining < 7) return 'bg-red-600';
    if (daysRemaining <= 14) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  return (
    <div
      data-testid="deposit-tracker-widget"
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden p-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2.5">
        <div className="flex items-center space-x-2.5">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">
            <i className="fas fa-hand-holding-dollar"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              Deposit Return Tracker
              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200">
                Texas § 92.103
              </span>
            </h3>
            <p className="text-[11px] text-gray-500">
              Mandatory 30-day post-surrender refund countdown
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={fetchDeposits}
            title="Refresh deposits (auto-refreshes every 5m)"
            disabled={loading}
            className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition"
          >
            <i className={'fas fa-sync-alt text-xs ' + (loading ? 'fa-spin text-emerald-600' : '')}></i>
          </button>
        </div>
      </div>

      {/* Error notification */}
      {error && (
        <div className="p-2 mb-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
          {error}
        </div>
      )}

      {loading && deposits.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-xs">
          <i className="fas fa-spinner fa-spin mr-2"></i>Loading deposits...
        </div>
      ) : deposits.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-xs">
          <i className="fas fa-check-circle text-emerald-500 text-lg mb-1 block"></i>
          No active deposit return countdowns.
        </div>
      ) : (
        <div className="space-y-3">
          {sortedDeposits.map((item) => {
            const percentRemaining = Math.max(0, Math.min(100, (item.daysRemaining / 30) * 100));
            const barColor = getProgressBarColor(item.daysRemaining);

            return (
              <div
                key={item.id}
                data-testid={'deposit-item-' + item.id}
                onClick={() => onDepositClick && onDepositClick(item)}
                className="p-3 bg-gray-50 hover:bg-gray-100/80 rounded-lg border border-gray-200/80 cursor-pointer transition-all"
              >
                <div className="flex items-start justify-between mb-1.5">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-gray-900 text-xs">
                        {item.tenantName}
                      </span>
                      {item.unitNumber && (
                        <span className="text-[11px] text-gray-500">
                          ({item.unitNumber})
                        </span>
                      )}
                    </div>
                    <p
                      onClick={(e) => {
                        if (onPropertyClick) {
                          e.stopPropagation();
                          onPropertyClick(item.propertyId);
                        }
                      }}
                      className="text-[11px] text-blue-600 hover:underline font-medium"
                    >
                      {item.propertyName}
                    </p>
                  </div>

                  <div className="text-right">
                    <div className="text-xs font-bold text-gray-900">
                      {'$' + item.depositAmount.toLocaleString()}
                    </div>
                    {getStatusBadge(item)}
                  </div>
                </div>

                {/* 30-Day Countdown Progress Bar */}
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                    <span>Surrender: {item.surrenderDate}</span>
                    <span>Deadline: {item.deadlineDate}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                    <div
                      role="progressbar"
                      aria-valuenow={item.daysRemaining}
                      aria-valuemin={0}
                      aria-valuemax={30}
                      style={{ width: item.daysRemaining < 0 ? '100%' : (percentRemaining + '%') }}
                      className={'h-1.5 rounded-full transition-all ' + barColor}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer Info */}
      <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
        <span>Sort: Urgency (Overdue first)</span>
        <span>Updated: {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    </div>
  );
};

export default DepositTrackerWidget;
